import React, { useLayoutEffect, useRef, useState } from "react";
import { apiAssetUrl } from "./api";
// Imported (not referenced as /business-card/... under public/) so webpack
// content-hashes the built filename, so editing one of these images always
// gets a new URL, instead of silently serving a stale browser-cached copy
// of the old pixels at the same path (found live 2026-09-16, after erasing
// the two bullet dots from front-fixed.png).
import frontFixedUrl from "./assets/business-card/front-fixed.png";
import backGoogleGIconUrl from "./assets/business-card/back-google-g-icon.png";
import backGoogleWordmarkUrl from "./assets/business-card/back-google-wordmark.png";
import backStarsUrl from "./assets/business-card/back-stars.png";

// All positions come from design/business-card-template/manifest.json,
// the pixel-accurate map of every editable slot and fixed element on the
// real printed template (2"x3.5" trim, 3.625"x2.125" with bleed, at 300
// DPI). Rendered as an SVG with this exact viewBox so every child scales
// together at any display size, with no separate font-size-scaling logic
// needed.
const CANVAS_W = 638;
const CANVAS_H = 1088;

// Trim is 600x1050 (2"x3.5" at 300 DPI) centered in the 638x1088 with-bleed
// canvas, a uniform ~19px (0.0625") bleed margin on every side. The safe
// zone is a further standard 1/8" (37.5px) inset from the trim line, same
// red-cut/blue-safe-zone convention as the PrintReady preflight tool this
// was modeled on.
const TRIM = { x: 19, y: 19, w: 600, h: 1050 };
const SAFE_INSET = 37.5;
const SAFE = {
  x: TRIM.x + SAFE_INSET, y: TRIM.y + SAFE_INSET,
  w: TRIM.w - SAFE_INSET * 2, h: TRIM.h - SAFE_INSET * 2,
};

const FRONT_SLOTS = {
  logo: { x: 135, y: 71, w: 367, h: 201 },
  address: { x: 101, y: 296, w: 435, h: 30 },
  phone: { x: 195, y: 348, w: 247, h: 23 },
  qr: { x: 131, y: 568, w: 376, h: 375 },
};

// Fred, 2026-09-16: the phone number (and the two bullet dots flanking it)
// sat too close under the address once it wrapped to two lines, so it moved to
// sit vertically centered in the gap between the address's last line and
// the fixed stars graphic below it, instead of the address/phone template's
// original single-line slot position. The dots are drawn as plain SVG
// circles at this same Y (erased from front-fixed.png, where they used to
// be baked in at a fixed pixel position) so they move together with the
// phone number rather than staying anchored to the old spot.
const STARS_TOP = 424; // measured from front-fixed.png, top of the star row
const DOT_RADIUS = 6.5;
const DOT_LEFT_X = 189; // center of the original left dot (was x 183-195)
const DOT_RIGHT_X = 447.5; // center of the original right dot (was x 442-453)

const BACK_SLOTS = {
  googleGIcon: { x: 123, y: 257, w: 391, h: 415 },
  googleWordmark: { x: 217, y: 148, w: 204, h: 68 },
  stars: { x: 135, y: 873, w: 368, h: 65 },
  headline: { x: 157, y: 80, w: 322, h: 31 },
  body: { x: 80, y: 731, w: 480, h: 93 },
  footer: { x: 67, y: 981, w: 501, h: 26 },
};

// Fixed back copy, never editable, confirmed with Fred 2026-09-15.
const BACK_TEXT = {
  headline: 'Votre avis nous intéresse',
  bodyLines: ['Prenez un moment pour nous laisser', 'un avis sur google'],
  footer: 'TOURNEZ LA CARTE POUR PLUS D’INFORMATION',
};

const FONT_FAMILY = "'Jost', sans-serif";

// Since one viewBox unit = 1px at 300 DPI = 1/300", a point (1/72") is
// 300/72 ≈ 4.1667 units. Fred, 2026-09-16: standard print body text on a
// business card is 7-8pt. Both address lines and the phone render at 29u
// (7pt). The address wraps onto two lines (street / city-province-postal)
// rather than shrinking to fit one, since the vertical gap between the logo
// and the phone slot's fixed dots doesn't have room for two 8pt lines.

interface BusinessCardPreviewProps {
  side: 'front' | 'back';
  restaurant: {
    address?: string | null;
    phone?: string | null;
    logoUrl?: string | null;
    logoScale?: number | null;
  };
  qrImageUrl?: string;
  cardBackgroundColor: string;
  cardTextColor: string;
  showGuides?: boolean;
}

const centerX = (box: { x: number; w: number }) => box.x + box.w / 2;
const centerY = (box: { y: number; h: number }) => box.y + box.h / 2;

// Fred, 2026-09-16: wrap the address onto two lines instead of shrinking it
// to fit one: street on line 1, city/province/postal on line 2, split at
// the first comma (the natural break in a "Street, City, Province Postal"
// address). Falls back to one line if there's no comma to split on.
const splitAddressLines = (address: string): [string, string?] => {
  const commaIndex = address.indexOf(',');
  if (commaIndex === -1) return [address, undefined];
  return [address.slice(0, commaIndex + 1), address.slice(commaIndex + 1).trim()];
};

/**
 * Renders one line of text at `baseFontSize`, shrinking it (once, after
 * measuring) only if it would render wider than `maxWidth`, found live
 * 2026-09-16: at the requested 7-8pt print size, a realistic address can
 * measure wider than the safe-zone margin (not the cut line itself, so it
 * wouldn't be physically clipped, but it breaches the safety margin those
 * guide lines exist to protect). This keeps 7-8pt as the target for the
 * common case and only steps down for addresses long enough to need it.
 */
const FitText: React.FC<{
  x: number; centerY: number; maxWidth: number; baseFontSize: number;
  fontFamily: string; fill: string; children: string;
}> = ({ x, centerY: cy, maxWidth, baseFontSize, fontFamily, fill, children }) => {
  const ref = useRef<SVGTextElement>(null);
  const [fontSize, setFontSize] = useState(baseFontSize);

  // Reset to the target size whenever the text or target changes, so a
  // shorter address typed in later can grow back up to full size.
  useLayoutEffect(() => {
    setFontSize(baseFontSize);
  }, [children, baseFontSize, maxWidth]);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const width = el.getBBox().width;
    if (width > maxWidth && fontSize === baseFontSize) {
      setFontSize(baseFontSize * (maxWidth / width));
    }
  }, [fontSize, baseFontSize, maxWidth, children]);

  return (
    <text
      ref={ref} x={x} y={cy + fontSize * 0.35}
      textAnchor="middle" fontFamily={fontFamily} fontSize={fontSize} fill={fill}
    >
      {children}
    </text>
  );
};

const BusinessCardPreview: React.FC<BusinessCardPreviewProps> = ({
  side, restaurant, qrImageUrl, cardBackgroundColor, cardTextColor, showGuides = false,
}) => {
  const logoSrc = apiAssetUrl(restaurant.logoUrl ?? undefined);
  const logoScale = Math.min(2.5, Math.max(0.5, restaurant.logoScale ?? 1));
  const [addressLine1, addressLine2] = restaurant.address ? splitAddressLines(restaurant.address) : [undefined, undefined];
  const ADDRESS_LINE_HEIGHT = 34;
  const ADDRESS_FONT_SIZE = 29;
  // Baseline of the address's last line (2 lines when there's a comma to
  // split on, 1 otherwise) stands in for "bottom of the text": this
  // address has no descenders in practice, and the rare one (a lowercase
  // g/y/p in a street or city name) overhangs by only a couple of units.
  const addressBottomY = (addressLine2
    ? centerY(FRONT_SLOTS.address) + ADDRESS_LINE_HEIGHT / 2
    : centerY(FRONT_SLOTS.address)) + ADDRESS_FONT_SIZE * 0.35;
  const phoneY = (addressBottomY + STARS_TOP) / 2;

  const guideOverlay = showGuides && (
    <>
      <rect x={TRIM.x} y={TRIM.y} width={TRIM.w} height={TRIM.h} fill="none" stroke="#DC2626" strokeWidth={2} strokeDasharray="6 4" />
      <rect x={SAFE.x} y={SAFE.y} width={SAFE.w} height={SAFE.h} fill="none" stroke="#2563EB" strokeWidth={2} strokeDasharray="6 4" />
    </>
  );

  return (
    <svg
      viewBox={`0 0 ${CANVAS_W} ${CANVAS_H}`}
      className="w-full h-auto rounded-md shadow"
      role="img"
      aria-label={side === 'front' ? 'Review card front' : 'Review card back'}
    >
      {side === 'front' ? (
        <>
          <rect x={0} y={0} width={CANVAS_W} height={CANVAS_H} fill="#FFFFFF" />
          {/* Fixed graphics: stars, "Compte Google requis" banner + frame,
              empty QR-slot outline, red pill button, bullet dots either
              side of the phone slot. */}
          <image href={frontFixedUrl} x={0} y={0} width={CANVAS_W} height={CANVAS_H} />
          {logoSrc && (
            <g>
              <clipPath id="card-logo-clip">
                <rect x={FRONT_SLOTS.logo.x} y={FRONT_SLOTS.logo.y} width={FRONT_SLOTS.logo.w} height={FRONT_SLOTS.logo.h} />
              </clipPath>
              <image
                href={logoSrc}
                x={centerX(FRONT_SLOTS.logo) - (FRONT_SLOTS.logo.w * logoScale) / 2}
                y={centerY(FRONT_SLOTS.logo) - (FRONT_SLOTS.logo.h * logoScale) / 2}
                width={FRONT_SLOTS.logo.w * logoScale}
                height={FRONT_SLOTS.logo.h * logoScale}
                preserveAspectRatio="xMidYMid meet"
                clipPath="url(#card-logo-clip)"
              />
            </g>
          )}
          {addressLine1 && (
            <FitText
              x={centerX(FRONT_SLOTS.address)}
              centerY={addressLine2 ? centerY(FRONT_SLOTS.address) - ADDRESS_LINE_HEIGHT / 2 : centerY(FRONT_SLOTS.address)}
              maxWidth={SAFE.w * 0.96} baseFontSize={ADDRESS_FONT_SIZE} fontFamily={FONT_FAMILY} fill="#1A1A1A"
            >
              {addressLine1}
            </FitText>
          )}
          {addressLine2 && (
            <FitText
              x={centerX(FRONT_SLOTS.address)} centerY={centerY(FRONT_SLOTS.address) + ADDRESS_LINE_HEIGHT / 2}
              maxWidth={SAFE.w * 0.96} baseFontSize={ADDRESS_FONT_SIZE} fontFamily={FONT_FAMILY} fill="#1A1A1A"
            >
              {addressLine2}
            </FitText>
          )}
          {restaurant.phone && (
            <>
              <circle cx={DOT_LEFT_X} cy={phoneY} r={DOT_RADIUS} fill="#1A1A1A" />
              <circle cx={DOT_RIGHT_X} cy={phoneY} r={DOT_RADIUS} fill="#1A1A1A" />
              <FitText
                x={centerX(FRONT_SLOTS.phone)} centerY={phoneY}
                maxWidth={FRONT_SLOTS.phone.w * 0.9} baseFontSize={29} fontFamily={FONT_FAMILY} fill="#1A1A1A"
              >
                {restaurant.phone}
              </FitText>
            </>
          )}
          {qrImageUrl && (
            <image
              href={qrImageUrl}
              x={FRONT_SLOTS.qr.x} y={FRONT_SLOTS.qr.y}
              width={FRONT_SLOTS.qr.w} height={FRONT_SLOTS.qr.h}
              preserveAspectRatio="xMidYMid meet"
            />
          )}
          {guideOverlay}
        </>
      ) : (
        <>
          <rect x={0} y={0} width={CANVAS_W} height={CANVAS_H} fill={cardBackgroundColor} />
          <image
            href={backGoogleGIconUrl}
            x={BACK_SLOTS.googleGIcon.x} y={BACK_SLOTS.googleGIcon.y}
            width={BACK_SLOTS.googleGIcon.w} height={BACK_SLOTS.googleGIcon.h}
          />
          <image
            href={backGoogleWordmarkUrl}
            x={BACK_SLOTS.googleWordmark.x} y={BACK_SLOTS.googleWordmark.y}
            width={BACK_SLOTS.googleWordmark.w} height={BACK_SLOTS.googleWordmark.h}
          />
          <image
            href={backStarsUrl}
            x={BACK_SLOTS.stars.x} y={BACK_SLOTS.stars.y}
            width={BACK_SLOTS.stars.w} height={BACK_SLOTS.stars.h}
          />
          <text
            x={centerX(BACK_SLOTS.headline)} y={centerY(BACK_SLOTS.headline) + 9}
            textAnchor="middle" fontFamily={FONT_FAMILY} fontWeight={600} fontSize={26} fill={cardTextColor}
          >
            {BACK_TEXT.headline}
          </text>
          <text
            x={centerX(BACK_SLOTS.body)} y={centerY(BACK_SLOTS.body) - 6}
            textAnchor="middle" fontFamily={FONT_FAMILY} fontWeight={500} fontSize={26} fill={cardTextColor}
          >
            {BACK_TEXT.bodyLines.map((line, i) => (
              <tspan key={i} x={centerX(BACK_SLOTS.body)} dy={i === 0 ? 0 : 32}>{line}</tspan>
            ))}
          </text>
          <text
            x={centerX(BACK_SLOTS.footer)} y={centerY(BACK_SLOTS.footer) + 7}
            textAnchor="middle" fontFamily={FONT_FAMILY} fontWeight={600} fontSize={17} letterSpacing={0.5} fill={cardTextColor}
          >
            {BACK_TEXT.footer}
          </text>
          {guideOverlay}
        </>
      )}
    </svg>
  );
};

export default BusinessCardPreview;
